/* Owned only by opted-in Shopify-hosted homepage background videos. */
if (!customElements.get('tlt-homepage-video')) {
  customElements.define('tlt-homepage-video', class extends HTMLElement {
    connectedCallback() {
      if (this._events) return;
      this._video = this.querySelector('video');
      this._button = this.querySelector('.tlt-video-toggle');
      if (!this._video || !this._button) return;
      this._events = new AbortController();
      this._generation = (this._generation || 0) + 1;
      const on = (target, name, fn) => target.addEventListener(name, fn, {signal:this._events.signal});
      this._motion = matchMedia('(prefers-reduced-motion: reduce)');
      this._connection = navigator.connection;
      this._visible = false;
      this._userPaused ??= false;
      this._optedIn ??= false;
      this._blocked = false;
      this._pending = false;
      this._video.muted = true;
      this._video.preload = 'none';
      this._button.hidden = false;
      on(this._button, 'click', () => {
        if (!this._video.paused || this._pending) {
          this._userPaused = true;
          this._video.pause();
        } else {
          this._userPaused = false;
          this._optedIn = true;
          this._blocked = false;
          this._reconcile();
        }
        this._sync();
      });
      on(this._video, 'playing', () => {
        if (!this._allowed()) { this._video.pause(); return; }
        this.setAttribute('data-video-ready', '');
        this._sync();
      });
      on(this._video, 'pause', () => this._sync());
      on(this._video, 'error', () => {
        this._blocked = true;
        this._video.pause();
        this.removeAttribute('data-video-ready');
        this._sync();
      });
      on(document, 'visibilitychange', () => this._reconcile());
      const preferencesChanged = () => {
        this._optedIn = false;
        if (this._prefersStill()) this.removeAttribute('data-video-ready');
        this._reconcile();
      };
      on(this._motion, 'change', preferencesChanged);
      if (this._connection?.addEventListener) on(this._connection, 'change', preferencesChanged);
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(entries => {
          const entry = entries[entries.length - 1];
          this._visible = entry.isIntersecting && entry.intersectionRatio >= .15;
          this._reconcile();
        }, {threshold:[0,.15]});
        this._observer.observe(this);
      } else {
        // Without visibility observation keep the still until explicit Play.
        this._visible = true;
        this._blocked = true;
      }
      this._sync();
    }
    disconnectedCallback() {
      this._observer?.disconnect();
      this._generation++;
      this._events?.abort();
      this._events = null;
      this._video?.pause();
    }
    _prefersStill() { return this._motion.matches || Boolean(this._connection?.saveData); }
    _allowed() {
      return Boolean(this._events && this._visible && !document.hidden && !this._userPaused && !this._blocked && (!this._prefersStill() || this._optedIn));
    }
    _sync() {
      const playing = !this._video.paused;
      this.toggleAttribute('data-video-playing', playing);
      this._button.setAttribute('aria-label', playing ? 'Pause promotional video' : 'Play promotional video');
    }
    _reconcile() {
      if (!this._allowed()) { this._video.pause(); this._sync(); return; }
      if (!this._video.paused || this._pending) return;
      this._pending = true;
      const generation = this._generation;
      let result;
      try { result = this._video.play(); }
      catch (error) { result = Promise.reject(error); }
      Promise.resolve(result).then(() => {
        if (generation !== this._generation) return;
        if (!this._allowed()) this._video.pause();
      }).catch(error => {
        if (generation !== this._generation) return;
        if (error?.name !== 'AbortError') {
          this._blocked = true;
          this.removeAttribute('data-video-ready');
        }
      }).finally(() => {
        if (generation !== this._generation) return;
        this._pending = false;
        this._sync();
        // A visibility change can abort play and return while that promise settles.
        if (this._allowed() && this._video.paused) this._reconcile();
      });
    }
  });
}
